import { useEffect, useState } from "react";
import User from "../users/User";

/**
 * The signed-in user, or `undefined` until `/v6/users/me` resolves.
 *
 * 🔴 THE RETURNED OBJECT IS KEYED `id`, NOT `uid`. `/v6/users/me` is declared
 * `response_model_by_alias=False` (aeronet-fast-api/app/users/api.py:267) over
 * `UserReadBase.id: int = Field(alias="uid")` (app/users/schemas.py:121), so
 * the wire key is the FIELD name `id`; `uid` is the ORM-side alias and is
 * absent from the response. Reading `.uid` yields `undefined`, and
 * `Number(undefined)` is NaN — which is silent, so it does not fail, it just
 * never becomes a number. This shipped a permanent spinner on Flight & Duty →
 * Limitations (the scope never resolved, so nothing was ever fetched and every
 * KPI read 0) and is hard-coded around in src/timesheets/TimesheetsWeek.tsx.
 * Read `.id`.
 *
 * ⚠️ FAILURE IS STILL TERMINAL FOR THE MOUNT. If the request rejects, this
 * returns `undefined` for the life of the component — there is no retry and no
 * error in the return value, so a consumer that gates its whole render on a
 * resolved user will wait forever. That contract is unchanged here on purpose:
 * ~15 modules destructure this as a bare object and widening it to
 * `{user, error}` is a cross-module refactor, not a bug fix. The failure is at
 * least no longer silent — see below. Consumers that gate rendering on this
 * should bound their own wait rather than assume the value must eventually
 * arrive.
 */
/*
 * The signed-in id accessor is `src/users/signedInUid.ts`, deliberately NOT an
 * export of this module: consumers mock this module wholesale
 * (`jest.mock("../../hooks/useCurrentUser", () => ({ useCurrentUser: ... }))`),
 * which would replace the accessor too. See the note in that file.
 */

export function useCurrentUser() {
  const [user, setUser] = useState<{ [key: string]: any }>();

  useEffect(() => {
    User.currentUser()
      .then((user) => {
        setUser(user);
      })
      .catch((err) => {
        // Was `.catch(() => {})`. An empty catch is why a failed `/users/me`
        // was indistinguishable from a slow one for every consumer of this
        // hook: no rejection propagated, no state changed, nothing was logged,
        // and the page simply waited on a value that was never coming. Keep
        // swallowing it — the contract above is deliberately unchanged — but
        // make it reach somebody.
        // eslint-disable-next-line no-console
        console.error("useCurrentUser: /v6/users/me failed; the signed-in user will stay undefined for this mount", err);
      });
  }, []);

  return user;
}
